import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { Button, ButtonGroup, Col, Container, Row, Badge } from "react-bootstrap";
import { Circle, Group, Line, Rect, Text } from "react-konva";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useParams, useNavigate } from "react-router-dom";

import VideoPlayer from "@/components/VideoPlayer";
import Preloader from "@/components/Elements/Preloader";
import { markupApi } from "@/api";

import "../../scss/components/_field.scss";

const DEFAULT_FPS = 25;

function buildFieldLines(fw, fh, pa, ga) {
  const paDepth = pa.length || 1650;
  const paWidth = pa.width || 4032;
  const gaDepth = ga.length || 550;
  const gaWidth = ga.width || 1832;
  const paTop = (fh - paWidth) / 2;
  const paBot = (fh + paWidth) / 2;
  const gaTop = (fh - gaWidth) / 2;
  const gaBot = (fh + gaWidth) / 2;

  return [
    { id: "touchline_far", label: "Дальняя боковая", color: "#FF4444", axis: "y", value: 0 },
    { id: "touchline_near", label: "Ближняя боковая", color: "#FF8844", axis: "y", value: fh },
    { id: "goal_line_left", label: "Левая лицевая", color: "#44DD44", axis: "x", value: 0 },
    { id: "goal_line_right", label: "Правая лицевая", color: "#44AAFF", axis: "x", value: fw },
    { id: "center_line", label: "Центральная", color: "#FFFF00", axis: "x", value: fw / 2 },
    { id: "penalty_l_front", label: "Штрафная Л перед", color: "#FF66AA", axis: "x", value: paDepth },
    { id: "penalty_l_far", label: "Штрафная Л дальн", color: "#FF99CC", axis: "y", value: paTop },
    { id: "penalty_l_near", label: "Штрафная Л ближн", color: "#FF99CC", axis: "y", value: paBot },
    { id: "penalty_r_front", label: "Штрафная П перед", color: "#AA66FF", axis: "x", value: fw - paDepth },
    { id: "penalty_r_far", label: "Штрафная П дальн", color: "#CC99FF", axis: "y", value: paTop },
    { id: "penalty_r_near", label: "Штрафная П ближн", color: "#CC99FF", axis: "y", value: paBot },
    { id: "goal_area_l_front", label: "Вратарская Л перед", color: "#66FFAA", axis: "x", value: gaDepth },
    { id: "goal_area_l_far", label: "Вратарская Л дальн", color: "#99FFCC", axis: "y", value: gaTop },
    { id: "goal_area_l_near", label: "Вратарская Л ближн", color: "#99FFCC", axis: "y", value: gaBot },
    { id: "goal_area_r_front", label: "Вратарская П перед", color: "#66AAFF", axis: "x", value: fw - gaDepth },
    { id: "goal_area_r_far", label: "Вратарская П дальн", color: "#99CCFF", axis: "y", value: gaTop },
    { id: "goal_area_r_near", label: "Вратарская П ближн", color: "#99CCFF", axis: "y", value: gaBot },
  ];
}

const LC_PREFIX = "lc_";

function linePointId(lineId, index) {
  return `${LC_PREFIX}${lineId}_${index}`;
}

function parseLinePointId(id) {
  if (!id.startsWith(LC_PREFIX)) return null;
  const rest = id.slice(LC_PREFIX.length);
  const lastUnderscore = rest.lastIndexOf("_");
  if (lastUnderscore === -1) return null;
  return {
    lineId: rest.slice(0, lastUnderscore),
    index: parseInt(rest.slice(lastUnderscore + 1), 10),
  };
}

export default function CalibrateCameraPage() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef();

  const [videoSource, setVideoSource] = useState(null);
  const [fieldSize, setFieldSize] = useState({ width: 10000, height: 6500 });
  const [penaltyArea, setPenaltyArea] = useState({ length: 1650, width: 4032 });
  const [goalArea, setGoalArea] = useState({ length: 550, width: 1832 });
  const [existingPoints, setExistingPoints] = useState([]);
  const [existingZonePoints, setExistingZonePoints] = useState([]);
  const [calibration, setCalibration] = useState(null);

  const [linePoints, setLinePoints] = useState([]);
  const [selectedLine, setSelectedLine] = useState(null);
  const [activePointId, setActivePointId] = useState(null);

  const [videoDomWidth, setVideoDomWidth] = useState(0);
  const [videoDomHeight, setVideoDomHeight] = useState(0);
  const [stageScale, setStageScale] = useState(1);
  const [saving, setSaving] = useState(false);

  const fieldLines = useMemo(
    () => buildFieldLines(fieldSize.width, fieldSize.height, penaltyArea, goalArea),
    [fieldSize, penaltyArea, goalArea]
  );

  const lineMap = useMemo(() => {
    const m = {};
    for (const l of fieldLines) m[l.id] = l;
    return m;
  }, [fieldLines]);

  // ── Load data ─────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    markupApi.getFieldMeta(matchId).then((res) => {
      if (cancelled) return;
      const { field: f, source, calibration: cal } = res;
      setVideoSource(source);
      setCalibration(cal);
      if (f.width) setFieldSize({ width: f.width, height: f.height });
      if (f.penalty_area_length) setPenaltyArea({ length: f.penalty_area_length, width: f.penalty_area_width });
      if (f.goal_area_length) setGoalArea({ length: f.goal_area_length, width: f.goal_area_width });
      setExistingPoints(f.points || []);

      const zps = f.zone_points || [];
      const lcPoints = [];
      const otherZps = [];
      for (const zp of zps) {
        if (zp.id?.startsWith(LC_PREFIX)) {
          lcPoints.push(zp);
        } else {
          otherZps.push(zp);
        }
      }
      setExistingZonePoints(otherZps);
      setLinePoints(lcPoints);
    });
    return () => { cancelled = true; };
  }, [matchId]);

  // ── Derived ───────────────────────────────────────────────────────────
  const w = videoDomWidth;
  const h = videoDomHeight;
  const lineWidth = 1 / stageScale;
  const hitWidth = 20 / stageScale;
  const fontSize = 11 / stageScale;
  const ptRadius = 5 / stageScale;

  const toAbsX = useCallback((x) => x * w, [w]);
  const toAbsY = useCallback((y) => y * h, [h]);

  const pointsByLine = useMemo(() => {
    const groups = {};
    for (const lp of linePoints) {
      const parsed = parseLinePointId(lp.id);
      if (!parsed) continue;
      if (!groups[parsed.lineId]) groups[parsed.lineId] = [];
      groups[parsed.lineId].push(lp);
    }
    return groups;
  }, [linePoints]);

  const totalLinePoints = linePoints.length;

  // ── Add point on click ────────────────────────────────────────────────
  const handleStageClick = useCallback(
    (e) => {
      if (!selectedLine || !w || !h) return;
      const target = e.target;
      const isPoint = target.attrs?.id?.startsWith(LC_PREFIX);
      if (isPoint) return;

      const stage = target.getStage();
      const pointer = stage.getPointerPosition();
      const transform = stage.getAbsoluteTransform().copy().invert();
      const pos = transform.point(pointer);
      const normX = pos.x / w;
      const normY = pos.y / h;

      if (normX < 0 || normX > 1 || normY < 0 || normY > 1) return;

      const existing = pointsByLine[selectedLine] || [];
      const newIndex = existing.length;
      const newPoint = {
        id: linePointId(selectedLine, newIndex),
        x: normX,
        y: normY,
        line_id: selectedLine,
      };
      setLinePoints((prev) => [...prev, newPoint]);
      setActivePointId(newPoint.id);
    },
    [selectedLine, w, h, pointsByLine]
  );

  // ── Drag handlers ─────────────────────────────────────────────────────
  const handleDragMove = useCallback(
    (e) => {
      if (!w || !h) return;
      const id = e.target.id();
      e.target.x(0);
      e.target.y(0);
      const pos = e.target.getRelativePointerPosition();
      setLinePoints((prev) =>
        prev.map((p) => (p.id === id ? { ...p, x: pos.x / w, y: pos.y / h } : p))
      );
    },
    [w, h]
  );

  const handlePointClick = useCallback((e) => {
    e.cancelBubble = true;
    setActivePointId(e.target.id());
  }, []);

  // ── Delete point ──────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if ((e.key === "Delete" || e.key === "Backspace") && activePointId) {
        setLinePoints((prev) => prev.filter((p) => p.id !== activePointId));
        setActivePointId(null);
      }
      if (e.key === "Escape") {
        setActivePointId(null);
        setSelectedLine(null);
      }
    };
    document.addEventListener("keyup", handler);
    return () => document.removeEventListener("keyup", handler);
  }, [activePointId]);

  // ── Save ──────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const allZonePoints = [
        ...existingZonePoints.map((p) => ({ id: p.id, x: p.x, y: p.y })),
        ...linePoints.map((p) => ({ id: p.id, x: p.x, y: p.y })),
      ];
      const body = {
        width: fieldSize.width,
        height: fieldSize.height,
        points: existingPoints.map((p) => ({ id: p.id, x: p.x, y: p.y })),
        zone_points: allZonePoints,
        penalty_area_length: penaltyArea.length || null,
        penalty_area_width: penaltyArea.width || null,
        goal_area_length: goalArea.length || null,
        goal_area_width: goalArea.width || null,
        goal_width: null,
        calibration: calibration,
      };
      await markupApi.updateFieldMeta(matchId, body);
      alert("Line calibration points saved!");
    } catch (error) {
      alert(`Save error: ${error}`);
    } finally {
      setSaving(false);
    }
  }, [matchId, existingPoints, existingZonePoints, linePoints, fieldSize, penaltyArea, goalArea, calibration]);

  const handleExportToFile = useCallback(() => {
    const data = {
      match_id: matchId,
      field: { width: fieldSize.width, height: fieldSize.height },
      penalty_area: { length: penaltyArea.length, width: penaltyArea.width },
      goal_area: { length: goalArea.length, width: goalArea.width },
      field_points: existingPoints.map((p) => ({ id: p.id, x: p.x, y: p.y })),
      line_constraint_points: linePoints.map((p) => {
        const parsed = parseLinePointId(p.id);
        const line = parsed ? lineMap[parsed.lineId] : null;
        return {
          id: p.id,
          x: p.x,
          y: p.y,
          line_id: parsed?.lineId || null,
          constraint_axis: line?.axis || null,
          constraint_value: line?.value ?? null,
        };
      }),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `calibration_${matchId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [matchId, fieldSize, penaltyArea, goalArea, existingPoints, linePoints, lineMap]);

  // ── VideoPlayer callbacks ─────────────────────────────────────────────
  const onVideoDomSizeChange = useCallback((width, height) => {
    setVideoDomWidth(width);
    setVideoDomHeight(height);
  }, []);

  const onStageScaleChangeCb = useCallback((scale) => {
    setStageScale(scale);
  }, []);

  const onStageMouseDown = useCallback(
    (e) => {
      const target = e.target;
      const isBackground =
        target === target.getStage() ||
        target.getClassName() === "Image" ||
        target.attrs.name === "field-bg";
      if (isBackground && !selectedLine) {
        setActivePointId(null);
      }
    },
    [selectedLine]
  );

  // ── Existing 9-pt overlay shape ───────────────────────────────────────
  const existingShapePoints = useMemo(() => {
    if (!w || !h || existingPoints.length < 4) return [];
    const pts = existingPoints;
    if (pts.length === 9) {
      const order = [0, 1, 2, 8, 3, 4, 5, 6];
      return order.flatMap((i) => [pts[i].x * w, pts[i].y * h]);
    }
    return pts.flatMap((p) => [p.x * w, p.y * h]);
  }, [existingPoints, w, h]);

  // ── Render ────────────────────────────────────────────────────────────
  if (!videoSource) return <Preloader />;

  const selectedLineDef = selectedLine ? lineMap[selectedLine] : null;

  return (
    <Container className="p-3" fluid>
      <Row className="mb-2">
        <Col>
          <div className="d-flex align-items-center gap-2">
            <Button variant="outline-secondary" size="sm" onClick={() => navigate(-1)}>
              <FontAwesomeIcon icon={["fas", "arrow-left"]} /> Back
            </Button>
            <h5 className="mb-0">Camera Line Calibration — Match {matchId}</h5>
            <Badge bg="info">{totalLinePoints} line points</Badge>
            <Badge bg="secondary">{existingPoints.length} field points</Badge>
          </div>
        </Col>
      </Row>

      {/* Line selector toolbar */}
      <Row className="mb-2">
        <Col>
          <div className="d-flex flex-wrap gap-1">
            <small className="text-muted me-1 align-self-center">Select line:</small>
            {fieldLines.map((line) => {
              const count = (pointsByLine[line.id] || []).length;
              const isSelected = selectedLine === line.id;
              return (
                <Button
                  key={line.id}
                  size="sm"
                  variant={isSelected ? "dark" : "outline-secondary"}
                  style={{
                    borderColor: line.color,
                    backgroundColor: isSelected ? line.color : undefined,
                    color: isSelected ? "#fff" : line.color,
                    fontSize: "0.75rem",
                    padding: "2px 8px",
                  }}
                  onClick={() => setSelectedLine(isSelected ? null : line.id)}
                >
                  {line.label} {count > 0 && <Badge bg="dark" pill>{count}</Badge>}
                </Button>
              );
            })}
          </div>
        </Col>
      </Row>

      {/* Status bar */}
      <Row className="mb-2">
        <Col>
          <div className="d-flex align-items-center gap-3">
            {selectedLineDef ? (
              <small>
                <strong style={{ color: selectedLineDef.color }}>{selectedLineDef.label}</strong>
                {" — "}click on the line in the video to add points.
                {" "}World constraint: <code>{selectedLineDef.axis} = {selectedLineDef.value} cm</code>
                {" | "}<kbd>Del</kbd> remove point, <kbd>Esc</kbd> deselect
              </small>
            ) : (
              <small className="text-muted">Select a field line above, then click on the video to place points along it.</small>
            )}
          </div>
        </Col>
      </Row>

      {/* Video player */}
      <Row>
        <Col>
          <div style={{ position: "relative" }}>
            <VideoPlayer
              ref={videoRef}
              src={videoSource}
              fps={DEFAULT_FPS}
              zoomButtonsVisible
              wheelZoomNeedsCtrl={false}
              aspectRatio="16 / 9"
              onVideoDomSizeChange={onVideoDomSizeChange}
              onStageScaleChange={onStageScaleChangeCb}
              onMouseDownHandler={onStageMouseDown}
              onClickHandler={handleStageClick}
            >
              {w > 0 && h > 0 && (
                <>
                  <Rect
                    name="field-bg"
                    width={w}
                    height={h}
                    fill="#000000"
                    opacity={0.1}
                  />

                  {/* Existing 9-pt field outline (dimmed) */}
                  {existingShapePoints.length > 0 && (
                    <Line
                      points={existingShapePoints}
                      stroke="rgba(255,255,255,0.3)"
                      strokeWidth={lineWidth}
                      closed
                    />
                  )}

                  {/* Existing 9 field points (dimmed) */}
                  {existingPoints.map((pt, i) => (
                    <Circle
                      key={`fp-${i}`}
                      x={toAbsX(pt.x)}
                      y={toAbsY(pt.y)}
                      radius={ptRadius * 0.7}
                      fill="rgba(255,255,255,0.4)"
                    />
                  ))}

                  {/* Line calibration points grouped by line */}
                  {fieldLines.map((line) => {
                    const pts = pointsByLine[line.id] || [];
                    if (pts.length === 0) return null;

                    const sortedPts = [...pts].sort((a, b) => a.x - b.x);
                    const lineCoords = sortedPts.flatMap((p) => [toAbsX(p.x), toAbsY(p.y)]);

                    return (
                      <Group key={`line-group-${line.id}`}>
                        {pts.length >= 2 && (
                          <Line
                            points={lineCoords}
                            stroke={line.color}
                            strokeWidth={lineWidth * 1.5}
                            opacity={0.6}
                          />
                        )}
                        {pts.map((pt) => {
                          const isActive = activePointId === pt.id;
                          const isSelectedLine = selectedLine === line.id;
                          return (
                            <Group
                              key={pt.id}
                              id={pt.id}
                              draggable
                              onDragMove={handleDragMove}
                              onClick={handlePointClick}
                              onTap={handlePointClick}
                            >
                              <Circle
                                id={pt.id}
                                x={toAbsX(pt.x)}
                                y={toAbsY(pt.y)}
                                radius={isActive ? ptRadius * 1.5 : ptRadius}
                                hitStrokeWidth={hitWidth}
                                fill={isActive ? "#fff" : line.color}
                                stroke={isSelectedLine ? "#fff" : line.color}
                                strokeWidth={lineWidth * (isActive ? 2 : 0.5)}
                                shadowColor="#000"
                                shadowBlur={isActive ? 8 : 3}
                                shadowOpacity={0.5}
                              />
                              {isActive && (
                                <Text
                                  x={toAbsX(pt.x) + ptRadius * 2}
                                  y={toAbsY(pt.y) - fontSize}
                                  text={`${line.label}`}
                                  fill="#fff"
                                  fontSize={fontSize}
                                />
                              )}
                            </Group>
                          );
                        })}
                      </Group>
                    );
                  })}
                </>
              )}
            </VideoPlayer>
          </div>
        </Col>
      </Row>

      {/* Bottom controls */}
      <Row className="mt-2">
        <Col className="d-flex gap-2">
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || totalLinePoints === 0}
          >
            {saving ? (
              <><FontAwesomeIcon spin icon={["fas", "spinner"]} /> Saving...</>
            ) : (
              <><FontAwesomeIcon icon={["fas", "save"]} /> Save ({totalLinePoints} points)</>
            )}
          </Button>
          <Button
            variant="outline-danger"
            onClick={() => {
              if (window.confirm("Clear all line calibration points?")) {
                setLinePoints([]);
                setActivePointId(null);
              }
            }}
            disabled={totalLinePoints === 0}
          >
            Clear all
          </Button>
          {selectedLine && (
            <Button
              variant="outline-warning"
              onClick={() => {
                const count = (pointsByLine[selectedLine] || []).length;
                if (count > 0 && window.confirm(`Clear all ${count} points on "${lineMap[selectedLine]?.label}"?`)) {
                  setLinePoints((prev) => prev.filter((p) => {
                    const parsed = parseLinePointId(p.id);
                    return parsed?.lineId !== selectedLine;
                  }));
                }
              }}
            >
              Clear "{lineMap[selectedLine]?.label}"
            </Button>
          )}
          <Button
            variant="outline-info"
            onClick={handleExportToFile}
            disabled={totalLinePoints === 0}
          >
            <FontAwesomeIcon icon={["fas", "download"]} /> Export JSON
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => navigate(`/panel/field/${matchId}`)}
          >
            <FontAwesomeIcon icon={["fas", "map-marker-alt"]} /> Field Markup
          </Button>
        </Col>
        <Col xs="auto">
          <small className="text-muted">
            Field: {fieldSize.width}×{fieldSize.height} cm |
            Penalty: {penaltyArea.length}×{penaltyArea.width} cm |
            Goal area: {goalArea.length}×{goalArea.width} cm
          </small>
        </Col>
      </Row>

      {/* Points summary table */}
      {totalLinePoints > 0 && (
        <Row className="mt-3">
          <Col>
            <details>
              <summary className="text-muted" style={{ cursor: "pointer" }}>
                Point details ({totalLinePoints} points on {Object.keys(pointsByLine).length} lines)
              </summary>
              <div className="mt-2" style={{ maxHeight: 300, overflow: "auto", fontSize: "0.8rem" }}>
                <table className="table table-sm table-striped">
                  <thead>
                    <tr>
                      <th>Line</th>
                      <th>Constraint</th>
                      <th>Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fieldLines.filter((l) => (pointsByLine[l.id] || []).length > 0).map((line) => (
                      <tr key={line.id}>
                        <td>
                          <span style={{ color: line.color, fontWeight: "bold" }}>{line.label}</span>
                        </td>
                        <td><code>{line.axis} = {line.value}</code></td>
                        <td>{(pointsByLine[line.id] || []).length}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          </Col>
        </Row>
      )}
    </Container>
  );
}
